export const stackOptions = {
  stackable: 0,
  nonStackable: 1,
}

export const promotionTypes = {
  applySelectedItem: 0,
  minimumOrder: 1,
  totalOrder: 2,
  freeItem: 3,
  deliveryFree: 4,
  discountNextItem: 5,
}

export const orderPromotionTypes = {
  price: 0,
  item: 1,
  deliveryOverride: 2,
  minimumOrderOverride: 3,
}

export const promotionAppliedOnTypes = {
  pickup: 0,
  delivery: 1,
  both: 2,
}

const itemPromotionTypes = [
  promotionTypes.freeItem,
  promotionTypes.discountNextItem,
]
export const isItemPromotion = (type?: number): boolean =>
  itemPromotionTypes.some((t) => t === type)

// Use only valid promotions and get selectedItems from orderItems
export const appendSelectedItems = (
  promotions: {
    id: string
    description: string
    quantity: number
    name: string
    type: number
    isPromoCodePromotion: boolean
    stackableOption: number
    promoOrder: number
    promotionItems: Oms.OrderItem[]
  }[],
  orderPromotions: {
    id: string
    promotionReference: string
    type: number
    amount: number
    name: string
    appliedItemReference: boolean
    resultAmountType: number
  }[],
  orderItems: Oms.OrderItem[],
): SelectedPromotion[] =>
  (promotions || [])
    .filter((promotion) =>
      (orderPromotions || []).some(
        (orderPromo) => orderPromo.promotionReference === promotion.id,
      ),
    )
    .map((promotion) => ({
      ...promotion,
      selectedItems: (orderItems || [])
        .filter(
          (orderItem) =>
            orderItem.isPromotionalItem &&
            orderItem.promotionReference === promotion.id,
        )
        .map((orderItem) => ({ ...orderItem, id: orderItem.id })),
    })) as SelectedPromotion[]

// Turn the item-quantity form values from the "Choose N item(s)" modal into the
// promotion's selectedItems. Quantities live in react-hook-form state and are
// only written once the customer touches a QuantitySelector, so an untouched
// form arrives here as an empty object.
export const buildSelectedItems = (
  promotionItems: SelectedPromotion['promotionItems'] | undefined,
  quantities: Record<string, number | undefined>,
): Oms.OrderItem[] =>
  (promotionItems || [])
    .filter((promotionItem) => quantities[promotionItem.id])
    .map((promotionItem) => ({
      ...promotionItem,
      quantity: quantities[promotionItem.id] as number,
    }))

// Total items picked across every QuantitySelector in the item modal.
export const countSelectedPromotionItems = (
  quantities: Record<string, number | undefined>,
): number =>
  Object.values(quantities).reduce<number>(
    (sum, value) =>
      typeof value === 'number' && value > 0 ? sum + value : sum,
    0,
  )

export const parsePromotion = (promotion: {
  id: string
  selectedItems?: {
    id: string
    itemReference: string
    quantity: number
  }[]
}): {
  promotionId: string
  variants: {
    variantId: string
    subitems: Record<string, unknown>[]
    quantity: number
  }[]
} => ({
  promotionId: promotion.id,
  variants: (promotion.selectedItems || []).map((item) => {
    return {
      variantId: item.itemReference || item.id,
      subitems: [],
      quantity: item.quantity,
    }
  }),
})

export const getValidPromotions = (
  nonPromoCodePromotions: Promotion[] = [],
  promoCodePromotions: Promotion[] = [],
) => {
  const unStackablePromo = nonPromoCodePromotions.find(
    (p) => p.stackableOption === stackOptions.nonStackable,
  )
  if (unStackablePromo) {
    return [unStackablePromo]
  } else {
    const unStackablePromoCode = promoCodePromotions.find(
      (p) => p.stackableOption === stackOptions.nonStackable,
    )
    if (unStackablePromoCode) {
      return [unStackablePromoCode]
    }
  }

  return nonPromoCodePromotions.concat(promoCodePromotions)
}
